const H17_FITMENT_DATA = window.H17_FITMENT_DATA || {};
const H17_FITMENT_TREE = H17_FITMENT_DATA.tree || {};
const H17_FITMENT_CONFIGURATIONS = H17_FITMENT_DATA.configurations || [];
const H17_PART_INDEX = H17_FITMENT_DATA.partIndex || {};
const H17_FITMENT_COLLATOR = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
const H17_COMPONENT_GROUPS = {
  lowerGrille: {
    label: 'Lower Grille Part Numbers',
    partLabel: 'Lower grille',
    originalRole: 'lowerGrilleOriginal',
    currentRole: 'lowerGrilleCurrent'
  },
  upperGrille: {
    label: 'Upper Grille Part Numbers',
    partLabel: 'Upper grille',
    originalRole: 'upperGrilleOriginal',
    currentRole: 'upperGrilleCurrent'
  }
};
const H17_COMPATIBILITY_LABELS = {
  compatible: 'Compatible',
  incompatible: 'Not compatible',
  mixed: 'Varies by part number',
  unknown: 'Not confirmed',
  notApplicable: 'Not applicable'
};

function h17SetOptions(select, values, placeholder) {
  select.innerHTML = '';
  select.append(new Option(placeholder, ''));
  values.forEach((value) => select.append(new Option(value, value)));
  select.disabled = values.length === 0;
  select.value = '';
}

function h17FitmentKey(fitment) {
  return JSON.stringify([
    fitment.brand,
    String(fitment.year),
    fitment.modelFamily,
    fitment.packageTrim,
    fitment.engine
  ]);
}

function h17CompatibilityLabel(status, partLookup = false) {
  if (partLookup && status === 'mixed') return 'Varies by fitment';
  return H17_COMPATIBILITY_LABELS[status] || H17_COMPATIBILITY_LABELS.unknown;
}

function h17AggregateCompatibility(statuses) {
  const values = [...new Set(statuses.filter(Boolean))];
  if (!values.length) return 'notApplicable';
  return values.length === 1 ? values[0] : 'mixed';
}

function h17ComponentGroupForRole(role) {
  if (role.startsWith('lowerGrille')) return 'lowerGrille';
  if (role.startsWith('upperGrille')) return 'upperGrille';
  return null;
}

function h17FormatComponentNumbers(fitment, group) {
  const definition = H17_COMPONENT_GROUPS[group];
  const originalNumbers = new Set(fitment.components?.[definition.originalRole] || []);
  const currentNumbers = new Set(fitment.components?.[definition.currentRole] || []);
  const allNumbers = [...new Set([...originalNumbers, ...currentNumbers])]
    .sort(H17_FITMENT_COLLATOR.compare);

  if (!allNumbers.length) return 'Not listed';

  return allNumbers.map((partNumber) => {
    const isOriginal = originalNumbers.has(partNumber);
    const isCurrent = currentNumbers.has(partNumber);
    if (isOriginal && isCurrent) return partNumber;
    return `${partNumber} (${isOriginal ? 'original' : 'current'})`;
  }).join(', ');
}

function h17SetCompatibilityValue(element, status, partLookup = false) {
  element.textContent = h17CompatibilityLabel(status, partLookup);
  element.className = `h17-finder__compatibility-value is-${status}`;
}

class H17KitFinder extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.h17-finder');
    this.lookupMethods = this.querySelectorAll('[name="lookup-method"]');
    this.vehicleFlow = this.querySelector('[data-lookup-panel="vehicle"]');
    this.partFlow = this.querySelector('[data-lookup-panel="part"]');
    this.brand = this.querySelector('[name="brand"]');
    this.year = this.querySelector('[name="year"]');
    this.modelFamily = this.querySelector('[name="model-family"]');
    this.packageTrim = this.querySelector('[name="package-trim"]');
    this.engine = this.querySelector('[name="engine"]');
    this.result = this.querySelector('.h17-finder__result');
    this.append(this.result);
    this.resultTitle = this.result.querySelector('.h17-finder__result-title');
    this.resultDescription = this.result.querySelector('.h17-finder__result-description');
    this.resultOptions = Object.fromEntries(
      ['lower', 'upper'].map((position) => [
        position,
        [...this.result.querySelectorAll(`[data-kit-position="${position}"]`)]
      ])
    );
    this.kitCards = new Map(
      [...this.result.querySelectorAll('[data-kit-key]')]
        .map((card) => [card.dataset.kitKey, card])
    );
    this.bothAction = this.querySelector('[data-h17-both-action]');
    this.cartStatus = this.querySelector('[data-h17-cart-status]');
    this.quickView = this.section?.querySelector('[data-h17-quick-view-dialog]');
    this.quickViewPosition = this.quickView?.querySelector('[data-h17-quick-view-position]');
    this.quickViewTitle = this.quickView?.querySelector('[data-h17-quick-view-title]');
    this.quickViewPrice = this.quickView?.querySelector('[data-h17-quick-view-price]');
    this.quickAdd = this.quickView?.querySelector('[data-h17-quick-add]');
    this.quickListing = this.quickView?.querySelector('[data-h17-quick-listing]');
    this.status = this.querySelector('.h17-finder__status');
    this.steps = this.querySelectorAll('.h17-finder__steps li');
    this.components = this.querySelector('.h17-finder__components');
    this.componentValues = Object.fromEntries(
      Object.keys(H17_COMPONENT_GROUPS).map((group) => [
        group,
        this.querySelector(`[data-component-group="${group}"]`)
      ])
    );
    this.compatibilityValues = Object.fromEntries(
      ['lowerLight', 'upperLight'].map((position) => [
        position,
        this.querySelector(`[data-compatibility="${position}"]`)
      ])
    );
    this.partSearchForm = this.querySelector('.h17-finder__part-search-form');
    this.partSearchInput = this.querySelector('[name="part-number"]');
    this.partSearchResults = this.querySelector('.h17-finder__part-results');
    this.partSearchSummary = this.querySelector('.h17-finder__part-summary');
    this.partSearchCards = this.querySelector('.h17-finder__part-cards');
    this.configurationByKey = new Map(
      H17_FITMENT_CONFIGURATIONS.map((fitment) => [h17FitmentKey(fitment), fitment])
    );

    this.lookupMethods.forEach((radio) => {
      radio.addEventListener('change', () => {
        this.resetFitmentOutput();
        this.selectLookupMethod();
      });
    });
    this.brand.addEventListener('change', () => this.updateYears());
    this.year.addEventListener('change', () => this.updateModelFamilies());
    this.modelFamily.addEventListener('change', () => this.updatePackageTrims());
    this.packageTrim.addEventListener('change', () => this.updateEngines());
    this.engine.addEventListener('change', () => this.updateKit());
    this.partSearchForm.addEventListener('submit', (event) => this.searchByPartNumber(event));
    this.querySelectorAll('[data-h17-add-kit]').forEach((button) => {
      button.addEventListener('click', () => this.addKits([button.dataset.h17AddKit], button));
    });
    this.querySelectorAll('[data-h17-add-pair]').forEach((button) => {
      button.addEventListener('click', () => {
        this.addKits(button.dataset.h17AddPair.split(','), button);
      });
    });
    this.querySelectorAll('[data-h17-quick-view]').forEach((button) => {
      button.addEventListener('click', () => this.openQuickView(button.dataset.h17QuickView));
    });
    this.quickView?.querySelector('[data-h17-close-quick-view]')?.addEventListener('click', () => this.closeQuickView());
    this.quickView?.addEventListener('click', (event) => {
      if (event.target === this.quickView) this.closeQuickView();
    });
    this.quickAdd?.addEventListener('click', () => {
      this.addKits([this.quickAdd.dataset.kitKey], this.quickAdd);
    });

    if (this.section?.dataset.wholesaleOrderingView === 'true') {
      this.querySelector('.h17-finder__lookup-mode').hidden = true;
      this.vehicleFlow.hidden = true;
      this.partFlow.hidden = true;
      this.showKitResults(
        ['lower', 'upper'],
        'Choose your wholesale kits.',
        'Choose Clear or Amber for each position, or add a matching upper and lower pair. No sled selection is required.'
      );
      return;
    }

    this.handleGlobalFitmentChange = (event) => {
      const fitment = event.detail?.fitment || null;
      const part = event.detail?.part || null;
      if (fitment && h17FitmentKey(fitment) === h17FitmentKey(this.selectedFitment() || {})) return;
      if (part && part.partNumber === this.partSearchInput.value) return;
      if (fitment) this.applySelectedFitment(fitment);
      else if (part) this.applySelectedPart(part);
      else {
        this.brand.value = '';
        this.partSearchInput.value = '';
        this.partSearchResults.hidden = true;
        this.updateYears();
      }
    };
    window.addEventListener('h17:fitment-change', this.handleGlobalFitmentChange);

    this.handleClearanceChange = () => {
      if (window.H17Fitment?.selectedPart) this.processPartNumbers([window.H17Fitment.selectedPart.partNumber], false);
      else this.updateKit(false);
    };
    window.addEventListener('h17:clearance-change', this.handleClearanceChange);
    this.updateYears();
    this.selectLookupMethod();
    if (window.H17Fitment?.selected) this.applySelectedFitment(window.H17Fitment.selected);
    else if (window.H17Fitment?.selectedPart) this.applySelectedPart(window.H17Fitment.selectedPart);
    else window.addEventListener('h17:fitment-ready', (event) => {
      if (event.detail?.fitment) this.applySelectedFitment(event.detail.fitment);
      else if (event.detail?.part) this.applySelectedPart(event.detail.part);
    }, { once: true });

    const requestedLookupMethod = new URLSearchParams(window.location.search).get('fitment');
    if (requestedLookupMethod === 'part' || requestedLookupMethod === 'vehicle') {
      const requestedMethod = this.querySelector(`[name="lookup-method"][value="${requestedLookupMethod}"]`);
      if (requestedMethod) requestedMethod.checked = true;
      this.selectLookupMethod();
      if (requestedLookupMethod === 'part' && window.H17Fitment?.selectedPart) {
        this.applySelectedPart(window.H17Fitment.selectedPart);
      } else if (requestedLookupMethod === 'vehicle' && window.H17Fitment?.selected) {
        this.applySelectedFitment(window.H17Fitment.selected);
      } else {
        this.resetFitmentOutput();
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('h17:clearance-change', this.handleClearanceChange);
    if (this.handleGlobalFitmentChange) {
      window.removeEventListener('h17:fitment-change', this.handleGlobalFitmentChange);
    }
  }

  selectLookupMethod() {
    const method = this.querySelector('[name="lookup-method"]:checked')?.value;
    this.vehicleFlow.hidden = method !== 'vehicle';
    this.partFlow.hidden = method !== 'part';
    if (method === 'part') this.partSearchInput.focus();
  }

  resetFitmentOutput() {
    this.result.hidden = true;
    Object.values(this.resultOptions).flat().forEach((option) => { option.hidden = true; });
    this.components.hidden = true;
    if (this.bothAction) this.bothAction.hidden = true;
    if (this.cartStatus) this.cartStatus.textContent = '';
    this.steps[1].classList.remove('is-active');
  }

  updateYears() {
    const years = Object.keys(H17_FITMENT_TREE[this.brand.value] || {})
      .sort((a, b) => Number(b) - Number(a));

    h17SetOptions(this.year, years, this.brand.value ? 'Select year' : 'Select brand first');
    h17SetOptions(this.modelFamily, [], 'Select brand and year first');
    h17SetOptions(this.packageTrim, [], 'Select model family first');
    h17SetOptions(this.engine, [], 'Select package/trim first');
    this.resetFitmentOutput();

    if (!Object.keys(H17_FITMENT_TREE).length) {
      this.status.textContent = 'Fitment data is temporarily unavailable.';
      return;
    }

    this.status.textContent = this.brand.value
      ? 'Select a year to continue.'
      : 'Select your brand to begin.';
  }

  updateModelFamilies() {
    const families = Object.keys(H17_FITMENT_TREE[this.brand.value]?.[this.year.value] || {})
      .sort(H17_FITMENT_COLLATOR.compare);

    h17SetOptions(this.modelFamily, families, this.year.value ? 'Select model family' : 'Select brand and year first');
    h17SetOptions(this.packageTrim, [], 'Select model family first');
    h17SetOptions(this.engine, [], 'Select package/trim first');
    this.resetFitmentOutput();
    this.status.textContent = this.year.value
      ? 'Select a model family to continue.'
      : 'Select a year to continue.';
  }

  updatePackageTrims() {
    const trims = Object.keys(
      H17_FITMENT_TREE[this.brand.value]?.[this.year.value]?.[this.modelFamily.value] || {}
    ).sort(H17_FITMENT_COLLATOR.compare);

    h17SetOptions(this.packageTrim, trims, this.modelFamily.value ? 'Select package/trim' : 'Select model family first');
    h17SetOptions(this.engine, [], 'Select package/trim first');
    this.resetFitmentOutput();
    this.status.textContent = this.modelFamily.value
      ? 'Select a package/trim to continue.'
      : 'Select a model family to continue.';
  }

  updateEngines() {
    const engines = (
      H17_FITMENT_TREE[this.brand.value]?.[this.year.value]?.[this.modelFamily.value]?.[this.packageTrim.value] || []
    ).slice().sort(H17_FITMENT_COLLATOR.compare);

    h17SetOptions(this.engine, engines, this.packageTrim.value ? 'Select engine' : 'Select package/trim first');
    this.resetFitmentOutput();
    this.status.textContent = this.packageTrim.value
      ? 'Select an engine to complete fitment.'
      : 'Select a package/trim to continue.';
  }

  selectedFitment() {
    return this.configurationByKey.get(h17FitmentKey({
      brand: this.brand.value,
      year: this.year.value,
      modelFamily: this.modelFamily.value,
      packageTrim: this.packageTrim.value,
      engine: this.engine.value
    }));
  }

  applySelectedFitment(fitment) {
    const vehicleMethod = this.querySelector('[name="lookup-method"][value="vehicle"]');
    if (vehicleMethod) vehicleMethod.checked = true;
    this.selectLookupMethod();
    this.brand.value = fitment.brand;
    this.updateYears();
    this.year.value = String(fitment.year);
    this.updateModelFamilies();
    this.modelFamily.value = fitment.modelFamily;
    this.updatePackageTrims();
    this.packageTrim.value = fitment.packageTrim;
    this.updateEngines();
    this.engine.value = fitment.engine;
    this.updateKit(false);
  }

  applySelectedPart(part) {
    const partMethod = this.querySelector('[name="lookup-method"][value="part"]');
    if (partMethod) partMethod.checked = true;
    this.selectLookupMethod();
    this.partSearchInput.value = part.partNumber;
    this.processPartNumbers([part.partNumber], false);
  }

  showComponents(fitment) {
    for (const group of Object.keys(H17_COMPONENT_GROUPS)) {
      this.componentValues[group].textContent = h17FormatComponentNumbers(fitment, group);
    }
    for (const position of Object.keys(this.compatibilityValues)) {
      const status = fitment.compatibility?.[position] || 'unknown';
      h17SetCompatibilityValue(this.compatibilityValues[position], status);
    }
    this.components.hidden = false;
  }

  updateKit(persistFitment = true) {
    this.resetFitmentOutput();
    const fitment = this.selectedFitment();

    if (!fitment) {
      this.status.textContent = 'Select brand, year, model family, package/trim, and engine to see component and light fitment.';
      return;
    }

    this.showComponents(fitment);
    if (persistFitment) {
      if (window.H17Fitment) window.H17Fitment.set(fitment);
      else window.addEventListener('h17:fitment-ready', () => window.H17Fitment?.set(fitment), { once: true });
    }

    const compatiblePositions = [];
    const clearance = window.H17Fitment?.bumperClearance || '';
    const lowerStatus = window.H17FitmentRules?.status(fitment, 'lower', clearance) || fitment.compatibility?.lowerLight;
    h17SetCompatibilityValue(this.compatibilityValues.lowerLight, lowerStatus);
    window.H17Fitment?.renderBumperChecks();
    if (lowerStatus === 'compatible') compatiblePositions.push('lower');
    if (fitment.compatibility?.upperLight === 'compatible') compatiblePositions.push('upper');

    if (!compatiblePositions.length) {
      const needsPartConfirmation = ['lowerLight', 'upperLight']
        .some((position) => fitment.compatibility?.[position] === 'mixed');
      this.status.textContent = window.H17FitmentRules?.reason(fitment, 'lower', clearance) || (needsPartConfirmation
        ? 'Compatibility varies by installed OEM grille. Search your exact lower or upper grille number before ordering.'
        : 'No confirmed Spot Pro installation position is listed for this fitment.');
      return;
    }

    const positionText = compatiblePositions.length === 2 ? 'upper or lower' : compatiblePositions[0];
    this.showKitResults(
      compatiblePositions,
      compatiblePositions.length === 2 ? 'Upper and lower Spot Pro kits fit this sled.' : `Your ${compatiblePositions[0]} Spot Pro kits are ready.`,
      compatiblePositions.length === 2
        ? 'Choose Clear or Amber for each compatible position, or add a matching-color upper and lower pair.'
        : `Choose a Clear or Amber light. Both colors use the same compatible ${positionText} fitment and bezels.`
    );
    this.steps[1].classList.add('is-active');
    this.status.textContent = `Spot Pro is confirmed for ${positionText} installation on this fitment.`;
  }

  showKitResults(compatiblePositions, title, description) {
    Object.entries(this.resultOptions).forEach(([position, options]) => {
      options.forEach((option) => { option.hidden = !compatiblePositions.includes(position); });
    });
    if (this.bothAction) this.bothAction.hidden = compatiblePositions.length !== 2;
    this.resultTitle.textContent = title;
    this.resultDescription.textContent = description;
    this.result.hidden = compatiblePositions.length === 0;
  }

  itemForKit(kitKey) {
    const card = this.kitCards.get(kitKey);
    if (!card?.dataset.variantId) throw new Error(`The ${kitKey} kit is not configured yet.`);
    if (card.dataset.available !== 'true') throw new Error(`The ${kitKey} kit is not open for presale yet.`);
    return { position: card.dataset.position, variantId: card.dataset.variantId };
  }

  async addKits(kitKeys, button) {
    if (this.quickView?.open) this.closeQuickView();
    if (!window.H17Fitment) {
      this.cartStatus.textContent = 'Ordering is still loading. Please try again.';
      return;
    }

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = kitKeys.length === 2 ? 'Adding Both…' : 'Adding…';
    this.cartStatus.textContent = '';
    try {
      const items = kitKeys.map((kitKey) => this.itemForKit(kitKey));
      const addedCart = await window.H17Fitment.addItems(items, button);
      const addedPositions = addedCart.h17AddedPositions || items.map(item => item.position);
      this.cartStatus.textContent = window.H17Fitment.isWholesaleOrder()
        ? kitKeys.length === 2
          ? 'The upper and lower presale kits were added to your wholesale order.'
          : 'The presale kit was added to your wholesale order.'
        : addedPositions.length === 2
          ? 'The upper and lower presale kits were added with your fitment and wiring choice.'
          : addedPositions.length === 1
            ? `The ${addedPositions[0]} kit was added with your verified fitment.`
            : 'Your single-kit choice has been saved. Review your cart.';
    } catch (error) {
      this.cartStatus.textContent = error.message || 'Unable to add the selected kit.';
    } finally {
      button.textContent = originalText;
      const unavailable = kitKeys.some((kitKey) => this.kitCards.get(kitKey)?.dataset.available !== 'true');
      button.disabled = unavailable;
    }
  }

  openQuickView(kitKey) {
    const card = this.kitCards.get(kitKey);
    if (!card || !this.quickView) return;
    const position = card.dataset.position;
    const color = card.dataset.color;
    this.quickViewPosition.textContent = `${position} installation · ${color} light`;
    this.quickViewTitle.textContent = card.querySelector('h4')?.textContent || 'Spot Pro Kit';
    const price = card.querySelector('.h17-finder__result-option-copy > p:not(.h17-eyebrow)');
    this.quickViewPrice.replaceChildren(...[...(price?.childNodes || [])].map((node) => node.cloneNode(true)));
    this.quickAdd.dataset.kitKey = kitKey;
    this.quickAdd.textContent = card.dataset.available === 'true'
      ? `Add ${color} ${position === 'lower' ? 'Lower' : 'Upper'} to Cart`
      : 'Presale Not Open';
    this.quickAdd.disabled = card.dataset.available !== 'true';
    this.quickListing.href = card.dataset.productUrl;
    if (this.quickView.showModal) this.quickView.showModal();
    else this.quickView.setAttribute('open', '');
  }

  closeQuickView() {
    if (this.quickView?.close) this.quickView.close();
    else this.quickView?.removeAttribute('open');
  }

  partCompatibility(partNumber) {
    if (window.H17FitmentRules) {
      const part = window.H17FitmentRules.part(partNumber);
      if (!part) return {roles:[],lowerLight:'unknown',upperLight:'unknown'};
      return {...part,lowerLight:window.H17FitmentRules.status(part,'lower',window.H17Fitment?.bumperClearance),upperLight:part.compatibility.upperLight};
    }
    const roles = new Set();
    const lowerStatuses = [];
    const upperStatuses = [];

    (H17_PART_INDEX[partNumber] || []).forEach((index) => {
      const fitment = H17_FITMENT_CONFIGURATIONS[index];
      if (!fitment) return;

      Object.entries(fitment.components || {}).forEach(([role, numbers]) => {
        if (!numbers.includes(partNumber)) return;
        const group = h17ComponentGroupForRole(role);
        if (!group) return;
        roles.add(group);

        const componentStatus = fitment.componentCompatibility?.[role]?.[partNumber];
        if (group === 'lowerGrille') {
          lowerStatuses.push(componentStatus || fitment.compatibility?.lowerLight || 'unknown');
        } else if (group === 'upperGrille') {
          upperStatuses.push(componentStatus || fitment.compatibility?.upperLight || 'unknown');
        } else {
          lowerStatuses.push(fitment.compatibility?.lowerLight || 'unknown');
          upperStatuses.push(fitment.compatibility?.upperLight || 'unknown');
        }
      });
    });

    return {
      roles: [...roles],
      lowerLight: h17AggregateCompatibility(lowerStatuses),
      upperLight: h17AggregateCompatibility(upperStatuses)
    };
  }

  renderPartCompatibility(partNumber, result) {
    const card = document.createElement('section');
    card.className = 'h17-finder__part-card';

    const heading = document.createElement('h4');
    heading.textContent = partNumber;

    const component = document.createElement('p');
    component.className = 'h17-finder__part-component';
    component.textContent = result.bumperPart ? result.name : result.roles.length
      ? result.roles.map((role) => H17_COMPONENT_GROUPS[role].partLabel).join(' / ')
      : 'No component record found';

    const grid = document.createElement('div');
    grid.className = 'h17-finder__compatibility-grid';
    ['lowerLight', 'upperLight'].forEach((position) => {
      const row = document.createElement('div');
      const label = document.createElement('span');
      label.textContent = position === 'lowerLight' ? 'Lower light' : 'Upper light';
      const value = document.createElement('strong');
      h17SetCompatibilityValue(value, result[position], true);
      row.append(label, value);
      grid.append(row);
    });

    card.append(heading, component, grid);
    this.partSearchCards.append(card);
  }

  searchByPartNumber(event) {
    event.preventDefault();
    const partNumbers = [...new Set(this.partSearchInput.value.match(/\d{6,}/g) || [])];
    this.processPartNumbers(partNumbers, true);
  }

  processPartNumbers(partNumbers, persistSelection = true) {
    this.resetFitmentOutput();
    this.partSearchCards.replaceChildren();
    this.partSearchResults.hidden = false;

    if (!partNumbers.length) {
      this.partSearchSummary.textContent = 'Enter a complete OEM grille part number using at least six digits.';
      return;
    }

    const found = [];
    const missing = [];
    partNumbers.forEach((partNumber) => {
      if (!(H17_PART_INDEX[partNumber] || []).length && !H17_FITMENT_DATA.obstructionParts?.[partNumber]) {
        missing.push(partNumber);
        return;
      }
      const result = this.partCompatibility(partNumber);
      found.push(partNumber);
      this.renderPartCompatibility(partNumber, result);
    });

    if (!found.length) {
      this.partSearchSummary.textContent = `No grille compatibility record was found for ${missing.join(' / ')}. Bottom-pan numbers are not accepted.`;
      return;
    }

    this.partSearchSummary.textContent = missing.length
      ? `Grille compatibility found for ${found.join(' / ')}. No grille record was found for ${missing.join(' / ')}.`
      : `Grille compatibility for ${found.join(' / ')}.`;

    if (found.length !== 1 || missing.length) {
      this.partSearchSummary.textContent += ' Search one known grille number at a time to save it and see matching kits.';
      return;
    }

    const partNumber = found[0];
    if (persistSelection) window.H17Fitment?.setPart(partNumber);
    const result = this.partCompatibility(partNumber);
    this.partSearchCards.replaceChildren();
    this.renderPartCompatibility(partNumber, result);
    window.H17Fitment?.renderBumperChecks();
    if (result.bumperPart) {
      this.partSearchSummary.textContent = result.reason;
      return;
    }
    const ambiguous = result.lowerLight === 'mixed' || result.upperLight === 'mixed';
    if (ambiguous) {
      this.partSearchSummary.textContent += ' Compatibility varies by sled configuration, so direct ordering is paused. Confirm the exact installed lower or upper grille number or use the year-and-sled search.';
      return;
    }

    const compatiblePositions = [];
    if (result.lowerLight === 'compatible') compatiblePositions.push('lower');
    if (result.upperLight === 'compatible') compatiblePositions.push('upper');

    if (!compatiblePositions.length) {
      this.partSearchSummary.textContent += ' ' + (window.H17FitmentRules?.reason(result, 'lower', window.H17Fitment?.bumperClearance) || 'No Spot Pro installation position is confirmed for this component.');
      return;
    }

    const positionText = compatiblePositions.length === 2 ? 'upper and lower' : compatiblePositions[0];
    this.showKitResults(
      compatiblePositions,
      compatiblePositions.length === 2
        ? `Upper and lower Spot Pro kits match OEM part ${partNumber}.`
        : `The ${compatiblePositions[0]} Spot Pro kits match OEM part ${partNumber}.`,
      `Verified for ${positionText} installation using this OEM grille lookup. Choose a Clear or Amber light; fitment is identical for both colors.`
    );
  }

}

if (!customElements.get('kit-finder')) customElements.define('kit-finder', H17KitFinder);
